import { Document } from 'flexsearch';

/**
 * In-memory FlexSearch index for city + hotel autocomplete (prefix-friendly).
 * Rebuilt when the catalog changes; queries are O(index lookup) instead of scanning full arrays.
 */

export function buildLocationSearchIndex(cities, hotels) {
  const doc = new Document({
    document: {
      id: 'id',
      // "full" keeps behavior close to String#includes (e.g. "mont" → Fairmont); forward-only misses infixes
      index: [{ field: 'text', tokenize: 'full', resolution: 9 }],
      store: true,
    },
  });

  const cityList = Array.isArray(cities) ? cities : [];
  const hotelList = Array.isArray(hotels) ? hotels : [];

  for (const city of cityList) {
    const sid = city.id || city.cityId || city.code;
    if (sid == null || String(sid).trim() === '') continue;
    const name = (city.name || city.cityName || city.city || '').toString().trim();
    const code = (city.code || city.cityId || city.id || '').toString();
    const country = (city.countryName || city.countryCode || '').toString();
    const text = [name, code, country].filter(Boolean).join(' ').toLowerCase();
    if (!text) continue;
    doc.add({
      id: `c:${String(sid)}`,
      text,
      _t: 'city',
      _sid: String(sid),
      _name: name || 'Unknown City',
      _code: code,
      _country: country,
    });
  }

  for (const h of hotelList) {
    const sid = h.id ?? h.code ?? h.propertyId;
    const name = (h.name || h.propertyName || h.hotelName || '').toString().trim();
    if (sid == null || String(sid).trim() === '' || !name) continue;
    const code = (h.code || '').toString();
    const cityName = (h.city || h.cityName || '').toString();
    const country = (h.countryName || h.countryCode || '').toString();
    const cityId = (h.cityId || h.city?.id || h.city?.cityId || '').toString().trim();
    const text = [name, code, cityName, country, cityId].filter(Boolean).join(' ').toLowerCase();
    if (!text) continue;
    doc.add({
      id: `h:${String(sid)}`,
      text,
      _t: 'property',
      _sid: String(sid),
      _name: name,
      _code: h.code,
      _city: cityName,
      _country: country,
      _cityId: cityId,
    });
  }

  return doc;
}

function docToSuggestion(d) {
  if (!d || !d._t) return null;
  if (d._t === 'city') {
    return {
      type: 'city',
      id: d._sid,
      name: d._name,
      code: d._code,
      country: d._country || '',
    };
  }
  return {
    type: 'property',
    id: d._sid,
    name: d._name,
    code: d._code,
    city: d._city || '',
    country: d._country || '',
    cityId: d._cityId || '',
  };
}

export function searchLocationIndex(index, query, opts = {}) {
  const limit = opts.limit ?? 24;
  const minLength = opts.minLength ?? 3;
  const q = (query || '').trim();
  if (!index || q.length < minLength) return [];

  const qLower = q.toLowerCase();
  let blocks;
  try {
    blocks = index.search(q, { enrich: true, limit: Math.min(80, limit * 4) });
  } catch (e) {
    console.warn('locationSearchIndex search failed:', e);
    return [];
  }

  const seen = new Set();
  const raw = [];
  for (const block of blocks || []) {
    for (const row of block.result || []) {
      const d = row.doc;
      const sug = docToSuggestion(d);
      if (!sug) continue;
      const dedupeKey =
        sug.type === 'city'
          ? `c:${(sug.name || '').toString().trim().toLowerCase()}`
          : `h:${String(sug.id)}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      raw.push(sug);
    }
  }

  const rank = (item) => {
    const n = (item.name || '').toLowerCase();
    const starts = n.startsWith(qLower);
    const inc = n.includes(qLower);
    if (item.type === 'property') {
      if (starts) return 0;
      if (inc) return 4;
      return 8;
    }
    if (starts) return 1;
    if (inc) return 5;
    return 9;
  };

  raw.sort((a, b) => {
    const d = rank(a) - rank(b);
    if (d !== 0) return d;
    return (a.name || '').localeCompare(b.name || '');
  });

  const cityHits = raw.filter((x) => x.type === 'city');
  const hotelHits = raw.filter((x) => x.type === 'property');
  const maxCities = Math.min(6, cityHits.length, limit);
  const topCities = cityHits.slice(0, maxCities);
  const remaining = Math.max(0, limit - topCities.length);
  const topHotels = hotelHits.slice(0, remaining);

  return [...topCities, ...topHotels];
}
